import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import styled from "styled-components";
import { LoginInfo } from "type/type";
import { adminLogin } from "api/api";
import { aesCBCEncrypt } from "utils/commonUtils";
import { BearSVG, ChildSVG, DinosaurSVG, GrassSVG, PenguinSVG } from "styles/global-styles";
import {VERSION} from "components/layout/SideMenu";
import { SolidPrimaryBtn } from "components/button/Button";
import { FlexWrapR, InputWrapC, InputWrapR } from "components/common/CommonLayout";
import { CommonInput, CustomCheckbox } from "components/input/Input";

const MainWrap = styled.main`
    width: 440px;
    padding: 2rem 1rem;
    background-color: ${({ theme }) => theme.rootWhite};
`;

const ImageWrap = styled.div`
  position: absolute;
  width: 100%;
  bottom: -185px;
  svg {
    position: absolute;
    max-height: 230px;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
  }
  .image-1 {
    left: -400px;
    width: 71px;
    height: 33px;
  }
  .image-2 {
    left: -245px;
    transform: rotateY(180deg);
    width: 99px;
    height: 152px;
  }
  .image-3 {
    width: 180px;
    height: 194px;
    left: -30px;
  }
  .image-4 {
    bottom: -20px;
    right: -100px;
    transform: rotateY(180deg);
    width: 95px;
    height: 98px;
  }
  .image-5 {
    right: -240px;
    width: 87px;
    height: 135px;
  }
  .image-6 {
    right: -460px;
    bottom: 50px;
    transform: rotateY(180deg);
    width: 125px;
    height: 58px;
  }
`;

function Login() {
    const navigate = useNavigate();

    const [isValid, setIsValid] = useState(false);
    const [eyeOnOff, setEyeOnOff] = useState(false);

    // 아이디 기억하기
    const [isRemember, setIsRemember] = useState(false);
    const [cookies, setCookie, removeCookie] = useCookies(["rememberId"]);

    // 아이디/패스워드
    const [loginForm, setLoginForm] = useState<LoginInfo>({
        login_id: "",
        login_pw: "",
        login_type: "WEB",
        mobile_token: "",
        mobile_os: 0,
        web_version: VERSION || "0.0.0",
    });

    // 비밀번호 입력값 보이기/가리기
    function toggleOnOff() {
        setEyeOnOff((prev) => !prev);
    }

    const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setLoginForm((prevData) => ({
            ...prevData,
            [name]: name === "login_pw" ? aesCBCEncrypt(value) : value,
        }));
    };

    // 엔터키
    function handleKeyDown(e:any) {
        if (e.key === 'Enter') {
            handleLogin();
        }
    }
    // 로그인 함수
    const handleLogin = async () => {
        try {
            const result = await adminLogin(loginForm);
            
            if (result.code === 200) {
                // 성공 시 cookie에 id 저장
                const expires = new Date();
                expires.setDate(expires.getDate() + 7);
                if (isRemember)
                    setCookie("rememberId", loginForm.login_id, {
                        path: "/",
                        expires,
                    });
                else removeCookie("rememberId");
                navigate("/");
            }
            else if (result.code === 403) {
                alert(`${result.message} 관리자에게 문의하시기 바랍니다.`);
                return;
            }
            else {
                alert("아이디 및 비밀번호를 다시 확인해주세요.");
                return;
            }
        } catch (error) {
            console.error("error ", error);
        }
    };

    // 아이디 기억하기
    function handleRemember(e: React.ChangeEvent<HTMLInputElement>) {
        setIsRemember(e.target.checked);
    }

    const openPolicy = (policyName:string) => {
        window.open(`/${policyName}.html`, '_blank');
    }

    // 아이디/비밀번호가 다 입력됐을 때 로그인 버튼 활성화
    useEffect(() => {
        if (loginForm.login_id && loginForm.login_pw) setIsValid(true);
        else setIsValid(false);
    }, [loginForm]);

    // 쿠키에 저장되어 있으면 가져오기
    useEffect(() => {
        if (cookies.rememberId !== undefined) {
            setLoginForm({ ...loginForm, login_id: cookies.rememberId });
            setIsRemember(true);
        }
    }, []);

  return (
      <MainWrap style={{minHeight: "auto"}}>
        <InputWrapC $gap="0.625rem">
            <label htmlFor="loginId" className="title">
                아이디
            </label>
            <CommonInput
                type="text"
                name="login_id"
                id="loginId"
                value={loginForm.login_id}
                onChange={handleChangeValue}
                placeholder="아이디 입력"
            />
        </InputWrapC>
        <InputWrapC $gap="0.625rem">
            <div>
                <label htmlFor="loginPw" className="title">비밀번호</label>
            </div>
            <CommonInput
                type={!eyeOnOff ? "password" : "text"}
                name="login_pw"
                id="loginPw"
                onChange={handleChangeValue}
                onKeyDown={handleKeyDown}
                placeholder="비밀번호 입력"
            />
            <div className="eye-on-off" onClick={toggleOnOff}>
                {!eyeOnOff ? <img src="/image/icon/eye_visible.svg" alt="eye_visible" /> : <img src="/image/icon/eye_invisible.svg" alt="eye_invisible" />}
            </div>
        </InputWrapC>
        <InputWrapR $alignItems="center">
            <CustomCheckbox
                type="checkbox"
                id="saveId"
                checked={isRemember}
                onChange={(e) => handleRemember(e)}
            />
            <label htmlFor="saveId">아이디 저장</label>
        </InputWrapR>
        <FlexWrapR $justifyContent="space-between" $alignItems="center" $gap="0.625rem">
            <Link to="/sign">회원가입</Link>
            <FlexWrapR $alignItems="center" style={{width: 'auto'}}>
                <Link to="/find/findLoginId">아이디 찾기</Link>
                <span>|</span>
                <Link to="/find/findLoginPw">비밀번호 찾기</Link>
            </FlexWrapR>
        </FlexWrapR>
        <FlexWrapR $alignItems="center" $justifyContent="center">
            <SolidPrimaryBtn
                type="button"
                className="login-btn"
                onClick={handleLogin}
                disabled={!isValid}>
                로그인
            </SolidPrimaryBtn>
        </FlexWrapR>
        <FlexWrapR $justifyContent="flex-end" style={{cursor: 'pointer'}}>
            <p onClick={() => {openPolicy('PersonalDataPolicy')}}>개인정보 처리방침</p>
        </FlexWrapR>

        <ImageWrap>
            <GrassSVG className="image-1"/>
            <BearSVG className="image-3"/>
            <DinosaurSVG className="image-2"/>
            <ChildSVG className="image-5"/>
            <PenguinSVG className="image-4"/>
            <GrassSVG className="image-6"/>
        </ImageWrap>
    </MainWrap>
  );
}

export default Login;
